import type { StateCreator } from 'zustand';
import { logError } from 'setup/logger/logError';
import { documentClient } from 'shared/domain/document/documentClient';
import type { DocumentModel } from 'shared/domain/document/documentModel';
import {
  getDocumentScopeKey,
  normalizeDocumentScopeQuery,
} from 'shared/domain/document/documentScope';
import { createLatestRead } from 'store/latestRead';
import { hasDocumentListChanged, shouldReloadDocumentsAfter } from './model';
import type { DocumentQuery, DocumentsState, DocumentsStoreState } from './types';

const getDefaultState = (): DocumentsState => ({
  documentsByScope: {},
  loadingScopes: {},
});

function withoutKey<TValue>(record: Record<string, TValue>, key: string): Record<string, TValue> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}

interface OpenScope {
  query: DocumentQuery;
  openCount: number;
  latestRead: ReturnType<typeof createLatestRead>;
}

export const createDocumentsSlice: StateCreator<DocumentsStoreState> = (set, get) => {
  // Bookkeeping of open scopes, not shown data: kept out of the state.
  const openScopes = new Map<string, OpenScope>();
  let stopFollowingChanges: (() => void) | null = null;

  const reloadOpenScopes = (): void => {
    openScopes.forEach((_scope, scopeKey) => void get().reloadScope(scopeKey));
  };

  const followChanges = (): void => {
    stopFollowingChanges ??= documentClient.onEvent((event) => {
      if (shouldReloadDocumentsAfter(event)) {
        reloadOpenScopes();
      }
    });
  };

  const stopFollowingChangesWhenClosed = (): void => {
    if (openScopes.size === 0) {
      stopFollowingChanges?.();
      stopFollowingChanges = null;
    }
  };

  const setScopeLoading = (scopeKey: string, loading: boolean): void =>
    set((state) => ({ loadingScopes: { ...state.loadingScopes, [scopeKey]: loading } }));

  const setScopeDocuments = (scopeKey: string, documents: DocumentModel[]): void =>
    set((state) => ({ documentsByScope: { ...state.documentsByScope, [scopeKey]: documents } }));

  return {
    ...getDefaultState(),

    openScope: (query) => {
      const normalizedQuery = normalizeDocumentScopeQuery(query);
      const scopeKey = getDocumentScopeKey(normalizedQuery);
      if (!normalizedQuery || !scopeKey) return;

      const openScope = openScopes.get(scopeKey);
      if (openScope) {
        openScope.openCount += 1;
        return;
      }

      openScopes.set(scopeKey, { query: normalizedQuery, openCount: 1, latestRead: createLatestRead() });
      followChanges();
      void get().reloadScope(scopeKey);
    },

    closeScope: (query) => {
      const scopeKey = getDocumentScopeKey(query);
      const openScope = scopeKey ? openScopes.get(scopeKey) : undefined;
      if (!scopeKey || !openScope) return;

      openScope.openCount -= 1;
      if (openScope.openCount > 0) return;

      openScope.latestRead.cancel();
      openScopes.delete(scopeKey);
      set((state) => ({
        documentsByScope: withoutKey(state.documentsByScope, scopeKey),
        loadingScopes: withoutKey(state.loadingScopes, scopeKey),
      }));
      stopFollowingChangesWhenClosed();
    },

    reloadScope: async (scopeKey) => {
      const openScope = openScopes.get(scopeKey);
      if (!openScope) return;

      if (!get().documentsByScope[scopeKey]?.length) {
        setScopeLoading(scopeKey, true);
      }

      await openScope.latestRead.run(() => documentClient.getOfParent(openScope.query), {
        onResult: (documents) => {
          if (hasDocumentListChanged(documents, get().documentsByScope[scopeKey] ?? [])) {
            setScopeDocuments(scopeKey, documents);
          }
          setScopeLoading(scopeKey, false);
        },
        onError: (error) => {
          logError('Could not read documents', { error, scopeKey });
          setScopeLoading(scopeKey, false);
        },
      });
    },

    reset: () => {
      openScopes.forEach((openScope) => openScope.latestRead.cancel());
      openScopes.clear();
      stopFollowingChangesWhenClosed();
      set(getDefaultState());
    },
  };
};
